// src/app/features/dashboard/widgets/ScarletWell/ScarletWellCheckInStyles.js
import { makeStyles } from '@material-ui/core/styles'
import { desktopSideNavWidth } from '../../../../navigation/navigationValues'

export const useScarletWellCheckInStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === 'dark'
  const headerGray = theme.color.midGray
  const borderGray = theme.color.white
  const radioBorder = theme.color.darkGray
  const surfaceColor = isDark
    ? theme.color.darkSlateGray
    : theme.palette.base.white
  const elevatedSurfaceColor = isDark
    ? theme.color.darkerSlateGray
    : theme.palette.base.white
  const dividerColor = isDark ? theme.color.gray : borderGray
  const headerBarColor = isDark ? theme.color.black : headerGray
  const radioBorderColor = isDark
    ? theme.color.secondaryHeaderTextDark
    : radioBorder
  const answerCellHoverColor = isDark
    ? 'rgba(71, 155, 191, 0.18)'
    : 'rgba(46, 131, 173, 0.08)'
  const answerCellActiveColor = isDark
    ? 'rgba(71, 155, 191, 0.28)'
    : 'rgba(46, 131, 173, 0.12)'
  const questionRowDarkColor = isDark
    ? theme.color.darkSlateGray
    : theme.color.lightGray
  const questionRowLightColor = isDark
    ? theme.color.darkGray
    : theme.color.lighterGray

  return {
    centerPage: {
      minHeight: '100vh',
      paddingTop: 65,
      paddingLeft: 55,
      background: theme.palette.background.default,
      [theme.breakpoints.down('sm')]: {
        paddingTop: 60,
        paddingLeft: 0,
      },
    },
    centerPageDrawerOpen: {
      minHeight: '100vh',
      paddingTop: 65,
      paddingLeft: desktopSideNavWidth,
      transition: 'width 0.4s',
      background: theme.palette.background.default,
    },
    pageContainer: {
      padding: theme.spacing(9.5, 2, 2, 2),
      [theme.breakpoints.down('sm')]: {
        padding: 0,
      },
    },
    stateContainer: {
      minHeight: 320,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    surveyShell: {
      maxWidth: 940,
      margin: '0 auto',
      borderRadius: '16px',
      boxShadow: isDark
        ? '0 8px 24px rgba(0, 0, 0, 0.35)'
        : '0 6px 16px rgba(0, 0, 0, 0.12)',
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
        borderRadius: '0px',
      },
    },
    introCard: {
      overflow: 'hidden',
      borderRadius: '16px 16px 0 0',
      background: isDark ? elevatedSurfaceColor : surfaceColor,
      boxShadow: isDark
        ? '0 8px 24px rgba(0, 0, 0, 0.35)'
        : '0 6px 16px rgba(0, 0, 0, 0.12)',
      [theme.breakpoints.down('sm')]: {
        borderRadius: 0,
        boxShadow: 'none',
      },
    },
    introHeader: {
      background: theme.color.headerBlue,
      color: theme.palette.base.white,
      padding: theme.spacing(2, 3),
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5, 2),
      },
    },
    introHeaderText: {
      color: theme.palette.base.white,
      fontWeight: 700,
    },
    introContent: {
      padding: theme.spacing(3, 2.5, 2.5),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2.5, 1.25, 1.5),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(2.25, 1, 1.25),
      },
    },
    introParagraph: {
      color: theme.palette.text.primary,
    },
    introSubText: {
      marginTop: theme.spacing(5),
      textAlign: 'center',
      color: theme.palette.text.primary,
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(3),
      },
    },
    introScaleText: {
      marginTop: theme.spacing(3),
      textAlign: 'center',
      color: theme.palette.text.primary,
      fontWeight: 700,
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2.5),
      },
    },
    introDivider: {
      marginTop: theme.spacing(3),
      backgroundColor: theme.palette.base.lightGray,
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2),
      },
    },
    categoryPanel: {
      background: theme.color.tealDark,
      marginTop: 0,
      padding: 0,
      [theme.breakpoints.down('sm')]: {
        marginTop: 0,
      },
    },
    categoryWarningBanner: {
      background: isDark ? theme.color.rutgersRed : surfaceColor,
      padding: theme.spacing(1.5, 2),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.25, 1.5),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1, 1),
      },
    },
    categoryWarningText: {
      margin: 0,
      color: theme.palette.primary.main,
      fontWeight: 700,
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.pxToRem(14),
      },
    },
    categoryGridWrapper: {
      padding: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.75),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.6),
      },
    },
    categoryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: theme.spacing(1.25),
      [theme.breakpoints.down('xs')]: {
        gap: theme.spacing(0.55),
      },
    },
    categoryButton: {
      minHeight: 68,
      borderRadius: 12,
      background: theme.color.tealLight,
      border: `4px solid ${theme.color.tealMedium}`,
      textTransform: 'none',
      [theme.breakpoints.down('sm')]: {
        minHeight: 56,
        padding: theme.spacing(0.75, 0.4),
        borderRadius: 10,
      },
      [theme.breakpoints.down('xs')]: {
        minHeight: 52,
        padding: theme.spacing(0.5, 0.2),
      },
    },
    activeCategoryButton: {
      background: theme.color.tealDark,
      border: '4px solid #ffffff',
      '&:hover': {
        background: theme.color.tealDark,
      },
    },
    invalidCategoryButton: {
      border: `3px solid ${theme.color.rutgersRed}`,
      boxShadow: `0 0 0 2px ${theme.color.rutgersRed}40`,
      '&:hover': {
        border: `3px solid ${theme.color.rutgersRed}`,
      },
    },
    categoryButtonText: {
      color: theme.palette.base.black,
      fontWeight: 700,
      textAlign: 'center',
      [theme.breakpoints.down('xs')]: {
        fontSize: '.825rem',
      },
    },
    activeCategoryButtonText: {
      color: theme.palette.base.white,
    },
    questionTable: {
      background: surfaceColor,
    },
    questionTableHeader: {
      display: 'grid',
      gridTemplateColumns: '1fr 420px',
      alignItems: 'center',
      padding: theme.spacing(2, 3),
      background: elevatedSurfaceColor,
      borderBottom: isDark ? `1px solid ${dividerColor}` : 'none',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr 360px',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        padding: theme.spacing(1.5, 1.5),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1.25, 0.5),
      },
    },
    tableHeaderLabel: {
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
    tableHeaderLabelRight: {
      textAlign: 'right',
      [theme.breakpoints.down('sm')]: {
        textAlign: 'right',
      },
    },
    answerScaleHeader: {
      position: 'sticky',
      top: 124,
      zIndex: 99,
      display: 'grid',
      gridTemplateColumns: '1fr 420px',
      alignItems: 'center',
      background: headerBarColor,
      color: theme.palette.base.white,
      padding: theme.spacing(1.75, 3),
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr 360px',
      },
      [theme.breakpoints.down('sm')]: {
        top: 0,
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        padding: theme.spacing(1.25, 1.5),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1, 0.5),
      },
    },
    answerScaleLabels: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      columnGap: theme.spacing(1),
      justifyItems: 'center',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'space-between',
        justifyItems: 'center',
        columnGap: theme.spacing(0.5),
      },
      [theme.breakpoints.down('xs')]: {
        columnGap: theme.spacing(0.5),
      },
    },
    answerScaleLabel: {
      color: theme.palette.base.white,
      fontWeight: theme.typography.fontWeightBold,
    },
    questionRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 420px',
      alignItems: 'center',
      padding: theme.spacing(2, 3),
      borderBottom: `1px solid ${dividerColor}`,
      background: questionRowLightColor,
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr 360px',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        columnGap: theme.spacing(1),
        padding: theme.spacing(1.5, 1.5),
      },
      [theme.breakpoints.down('xs')]: {
        columnGap: theme.spacing(0.75),
        padding: theme.spacing(1.25, 0.5),
      },
    },
    questionRowAlternate: {
      background: questionRowDarkColor,
    },
    questionText: {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightBold,
      paddingRight: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        paddingRight: 0,
      },
    },
    answerScale: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      columnGap: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'space-between',
        columnGap: theme.spacing(0.5),
      },
      [theme.breakpoints.down('xs')]: {
        columnGap: theme.spacing(0.5),
      },
    },
    answerCell: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 52,
      cursor: 'pointer',
      borderRadius: 10,
      padding: theme.spacing(0.75, 0.5),
      '&:hover': {
        background: answerCellHoverColor,
      },
      '&:focus-within': {
        border: `2px solid ${
          isDark ? theme.color.tealLight : theme.color.tealDark
        }`,
        outline: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: 46,
        padding: theme.spacing(0.3, 0.2),
      },
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    answerCellActive: {
      background: answerCellActiveColor,
    },
    hiddenInput: {
      position: 'absolute',
      opacity: 0,
      pointerEvents: 'none',
    },
    radioOuter: {
      width: 24,
      height: 24,
      borderRadius: '50%',
      border: `3px solid ${radioBorderColor}`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      background: surfaceColor,
      [theme.breakpoints.down('sm')]: {
        width: 22,
        height: 22,
        borderWidth: 3,
      },
    },
    radioOuterChecked: {
      borderColor: isDark ? theme.palette.primary.headerText : '#3d3d3d',
    },
    radioInner: {
      width: 12,
      height: 12,
      borderRadius: '50%',
      background: 'transparent',
      [theme.breakpoints.down('sm')]: {
        width: 12,
        height: 12,
      },
    },
    radioInnerChecked: {
      background: isDark ? theme.color.tealLight : theme.color.tealMedium,
    },
    mobileOptionLabel: {
      display: 'none',
      marginTop: theme.spacing(0.75),
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    emptyState: {
      padding: theme.spacing(4),
      background: surfaceColor,
      textAlign: 'center',
      color: theme.palette.text.primary,
    },
    mobileFooterActions: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1),
      background: surfaceColor,
      borderTop: `1px solid ${headerBarColor}`,
      borderRadius: '0 0 16px 16px',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5, 1),
        borderRadius: 0,
      },
    },
    mobileFooterSide: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 120,
      [theme.breakpoints.down('sm')]: {
        minWidth: 88,
      },
    },
    mobileFooterSideRight: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
    },
    mobileNavButton: {
      background: theme.color.tealDark,
      color: theme.palette.base.white,
      fontWeight: 700,
      borderRadius: 8,
      height: 40,
      minWidth: 120,
      [theme.breakpoints.down('sm')]: {
        minWidth: 96,
      },
    },
    mobileSubmitButtonDisabled: {
      background: theme.color.lightGray,
      fontWeight: 700,
      borderRadius: 8,
      height: 40,
      minWidth: 120,
      [theme.breakpoints.down('sm')]: {
        minWidth: 96,
      },
    },
    mobileSubmitButton: {
      background: theme.color.tealPale,
      color: theme.palette.base.black,
      fontWeight: 700,
      borderRadius: 8,
      height: 40,
      minWidth: 120,
      border: `2px solid ${theme.color.tealDark}`,
      [theme.breakpoints.down('sm')]: {
        minWidth: 96,
      },
    },
    buttonText: {
      fontWeight: 700,
    },
    buttonTextDisabled: {
      fontWeight: 700,
      color: theme.color.gray,
    },
    modalContent: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
    },
  }
})

export default useScarletWellCheckInStyles
